/** ToDo Remove this after dummy data is removed */

import dayjs from '@aqx-core/common/dayjs';

import { EOptionSymbolSide } from '../../market';
import { TOptionChainData, TOptionStrikeDetail } from '../model';

interface IGenerateOptionChainDataParams {
  numArrays: number;
  symbolName: string;
  symbolPrice: number;
  expiration?: string;
  strikesCount?: number;
  strikePriceIncrement?: number;
}

const calculateBidAsk = (strikePrice: number, symbolPrice: number) => {
  const priceDiff = Math.abs(strikePrice - symbolPrice);
  const strikeDiffSteps = Math.floor(priceDiff / 2.5);

  let callBid = 1.53;
  let callAsk = 1.64;
  let putBid = 1.53;
  let putAsk = 1.64;

  if (strikePrice < symbolPrice) {
    // For calls: Increase prices for lower strikes
    callBid += strikeDiffSteps * 2.5;
    callAsk += strikeDiffSteps * 2.5;
    // For puts: Decrease prices for lower strikes
    putBid = Math.max(0, putBid - strikeDiffSteps * 0.1);
    putAsk = Math.max(0, putAsk - strikeDiffSteps * 0.1);
  } else if (strikePrice > symbolPrice) {
    // For calls: Decrease prices for higher strikes
    callBid = Math.max(0, callBid - strikeDiffSteps * 0.1);
    callAsk = Math.max(0, callAsk - strikeDiffSteps * 0.1);
    // For puts: Increase prices for higher strikes
    putBid += strikeDiffSteps * 2.5;
    putAsk += strikeDiffSteps * 2.5;
  }

  return { callBid, callAsk, putBid, putAsk };
};

export const generateOptionChainData = async ({
  numArrays,
  symbolName,
  symbolPrice,
  expiration,
  strikesCount = 20,
  strikePriceIncrement = 0.5
}: IGenerateOptionChainDataParams) => {
  // Prevent race condition
  await new Promise((resolve) => setTimeout(resolve, 200));

  const dummyData: TOptionChainData[] = [];

  const numIterations = expiration ? 1 : numArrays;

  for (let i = 0; i < numIterations; i++) {
    const expirationTime =
      expiration ||
      dayjs()
        .add(3 + i * 7, 'day')
        .format('YYYYMMDD'); // Start with 3 days, increment by 7 days
    const strikes: TOptionStrikeDetail[] = [];
    const baseStrikePrice = symbolPrice - (strikesCount / 2) * strikePriceIncrement; // Start 10 strikes below symbol price

    for (let j = 0; j < strikesCount; j++) {
      const strikePrice = baseStrikePrice + j * strikePriceIncrement;

      const { callBid, callAsk, putBid, putAsk } = calculateBidAsk(strikePrice, symbolPrice);

      strikes.push({
        strikePrice: strikePrice,
        call: {
          symbol: `${symbolName}${dayjs(expiration).format('YYYYMMDD')}C${strikePrice}`,
          expiration: expirationTime,
          strikePrice: strikePrice,
          bid: callBid,
          ask: callAsk,
          lastPrice: (callBid + callAsk) / 2,
          volume: 1000,
          openInterest: 500,
          impliedVolatility: 0.2,
          delta: 0.6,
          gamma: 0.03,
          vega: 0.04,
          theta: -0.02,
          bep: 0.5,
          probability: 0.4,
          side: EOptionSymbolSide.CALL,
          tickRulesGroup: []
        },
        put: {
          symbol: `${symbolName}${dayjs(expiration).format('YYYYMMDD')}P${strikePrice}`,
          expiration: expirationTime,
          strikePrice: strikePrice,
          bid: putBid,
          ask: putAsk,
          lastPrice: (putBid + putAsk) / 2,
          volume: 800,
          openInterest: 600,
          impliedVolatility: 0.18,
          delta: -0.4,
          gamma: 0.02,
          vega: 0.03,
          theta: -0.015,
          bep: -0.5,
          probability: 0.6,
          side: EOptionSymbolSide.PUT,
          tickRulesGroup: []
        }
      });
    }

    dummyData.push({
      expiration: expirationTime,
      daysToExpiration: dayjs(expirationTime).diff(dayjs(), 'day'),
      optionPriceList: strikes
    });
  }

  return dummyData;
};
